import React, { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { XIcon } from '@heroicons/react/outline';
import { apiRecordConsentWithdrawal } from '../../utils/rmsApi';

/*
 * RecordConsentWithdrawalDialog (TDI-476)
 *
 * BTH staff record that a parent or carer told them they are withdrawing
 * consent for the referral: when they said it, how, and a note. The server
 * (rmsRecordConsentWithdrawal) stops the referral through the same engine a
 * family withdrawal uses, so the two routes cannot drift.
 *
 * Built on the clinician workspace's dialog pattern (SendTriageLetterDialog).
 * The confirm is destructive (semantic red, consequence spelled out), never
 * plum or gold. No design frame exists for the BTH side; wording is a draft
 * for Jasmine's gate.
 */

const METHODS = [
  { value: 'phone', label: 'By phone' },
  { value: 'email', label: 'By email' },
  { value: 'other', label: 'Another way' },
];

function todayIso() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default function RecordConsentWithdrawalDialog({
  open,
  onOpenChange,
  orgId,
  patientRecordId,
  childName,
  onRecorded,
}) {
  const [withdrawnOn, setWithdrawnOn] = useState(todayIso);
  const [method, setMethod] = useState('phone');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // The second of the two confirmations FR7 asks for.
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (open) {
      setWithdrawnOn(todayIso());
      setMethod('phone');
      setNote('');
      setBusy(false);
      setError(null);
    }
  }, [open]);

  const noteRequired = method === 'other';
  const canSubmit = Boolean(withdrawnOn) && (!noteRequired || note.trim().length > 0) && !busy;

  // TDI-470 FR7 asks staff to confirm twice before a withdrawal they were
  // told about is recorded: the first submit asks for the confirmation, the
  // second commits. Recording one stops the referral for everyone and emails
  // the family and the school, and it is not a thing to do by a stray click.
  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    if (!confirming) {
      setConfirming(true);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await apiRecordConsentWithdrawal({
        orgId, patientRecordId, withdrawnOn, method, note: note.trim() || undefined,
      });
      if (typeof onRecorded === 'function') onRecorded(result);
      onOpenChange(false);
    } catch (err) {
      setError(err?.message || 'Could not record the withdrawal. Try again.');
      // Back to the form, so nothing commits on a second click at a dialog
      // the caller has not read the refusal on.
      setConfirming(false);
      setBusy(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(next) => { if (!busy) onOpenChange(next); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content
          aria-describedby="record-withdrawal-intro"
          className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white p-6 shadow-xl focus:outline-none"
        >
          <div className="flex items-start justify-between gap-4">
            <Dialog.Title className="text-lg font-semibold text-gray-900">
              Record a withdrawal of consent
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Close"
                disabled={busy}
                className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>
          <Dialog.Description id="record-withdrawal-intro" className="mt-2 text-sm text-gray-700">
            Use this when a parent or carer has told the service they are withdrawing
            consent for {childName ? `${childName}'s` : 'this'} referral. It stops the
            referral straight away.
          </Dialog.Description>

          <form onSubmit={submit} className="mt-5 space-y-5">
            <div>
              <label htmlFor="withdrawal-date" className="block text-sm font-medium text-gray-900">
                When did they tell you?
              </label>
              <p className="mt-0.5 text-sm text-gray-500">The date they said it, not today&rsquo;s date if that was earlier.</p>
              <input
                id="withdrawal-date"
                type="date"
                required
                max={todayIso()}
                value={withdrawnOn}
                onChange={(e) => setWithdrawnOn(e.target.value)}
                className="mt-2 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-primary focus:ring-primary"
              />
            </div>

            <fieldset>
              <legend className="text-sm font-medium text-gray-900">How did they tell you?</legend>
              <div className="mt-2 space-y-2">
                {METHODS.map((m) => (
                  <label key={m.value} className="flex items-center gap-3 text-sm text-gray-800">
                    <input
                      type="radio"
                      name="withdrawal-method"
                      value={m.value}
                      checked={method === m.value}
                      onChange={() => setMethod(m.value)}
                      className="h-4 w-4 text-primary focus:ring-primary"
                    />
                    {m.label}
                  </label>
                ))}
              </div>
            </fieldset>

            <div>
              <label htmlFor="withdrawal-note" className="block text-sm font-medium text-gray-900">
                Note{noteRequired ? '' : ' (optional)'}
              </label>
              <p className="mt-0.5 text-sm text-gray-500">
                {noteRequired
                  ? 'Say how they told you, so the record is complete.'
                  : 'Anything the team should know, for example who called.'}
              </p>
              <textarea
                id="withdrawal-note"
                rows={3}
                maxLength={2000}
                required={noteRequired}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="mt-2 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-primary focus:ring-primary"
              />
            </div>

            {error && (
              <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <p className="text-sm text-gray-700">
              This cannot be undone here. Only the parent or carer can give their consent again.
            </p>

            {confirming && (
              <div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-gray-900">
                Recording this stops the referral and tells the parent or carer
                and the school. Choose &ldquo;Yes, record the withdrawal&rdquo;
                to go ahead.
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Dialog.Close asChild>
                <button
                  type="button"
                  disabled={busy}
                  className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
              </Dialog.Close>
              <button
                type="submit"
                disabled={!canSubmit}
                aria-busy={busy}
                className="inline-flex items-center rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy
                  ? 'Recording…'
                  : confirming
                    ? 'Yes, record the withdrawal'
                    : 'Record the withdrawal'}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
